import { useState } from "react";

const idsKept = (allIds, custom) =>
  Array.isArray(custom) ? allIds.filter((id) => custom.includes(id)) : allIds;

/**
 * Nested keep/hide for a product's choices and their items —
 * same interaction as subcategory → products on a category.
 */
const ProductChoiceKeepPanel = ({
  productId,
  choices = [],
  keptChoiceIds,
  onKeptChoicesChange,
  keptItems = {},
  onKeptItemsChange,
  t,
}) => {
  const [open, setOpen] = useState({});
  const allChoiceIds = choices.map((c) => c.id);
  const keptChoices = idsKept(allChoiceIds, keptChoiceIds);

  const toggleChoice = (id) =>
    onKeptChoicesChange?.(
      keptChoices.includes(id)
        ? keptChoices.filter((x) => x !== id)
        : [...keptChoices, id]
    );

  return (
    <div className="menu-picker__children">
      {choices.map((choice) => {
        const choiceOn = keptChoices.includes(choice.id);
        const items = Array.isArray(choice.products) ? choice.products : [];
        const itemIds = items.map((p) => p.id);
        const keptItemIds = idsKept(itemIds, keptItems[choice.id]);
        const panelKey = `${productId}:${choice.id}`;
        const hasPanel = choiceOn && items.length > 0;
        const panelOpen = hasPanel && (open[panelKey] ?? false);
        const switchId = `choice-keep-${panelKey}`;
        return (
          <div
            key={choice.id}
            className={`menu-picker__child ${choiceOn ? "is-on" : ""} ${
              panelOpen ? "is-expanded" : ""
            }`}
          >
            <div className="menu-picker__head">
              <label
                htmlFor={switchId}
                className={`menu-picker__product ${choiceOn ? "is-on" : ""}`}
              >
                <span className="menu-picker__avatar menu-picker__avatar--folder" aria-hidden>
                  <i className="fas fa-list" />
                </span>
                <span className="menu-picker__product-label">{choice.label}</span>
                {items.length > 0 && (
                  <span className={`menu-picker__badge ${choiceOn ? "menu-picker__badge--kept" : ""}`}>
                    {choiceOn
                      ? t("menuTreeKeptBadge", {
                          kept: keptItemIds.length,
                          total: items.length,
                        })
                      : t("menuTreeItemsBadge", { count: items.length })}
                  </span>
                )}
                <input
                  id={switchId}
                  type="checkbox"
                  className="menu-picker__switch"
                  role="switch"
                  checked={choiceOn}
                  aria-checked={choiceOn}
                  onChange={() => toggleChoice(choice.id)}
                />
              </label>
              {hasPanel && (
                <button
                  type="button"
                  className={`btn menu-picker__chevron ${panelOpen ? "is-open" : ""}`}
                  onClick={() =>
                    setOpen((prev) => ({ ...prev, [panelKey]: !(prev[panelKey] ?? false) }))
                  }
                  aria-expanded={panelOpen}
                >
                  <i className="fas fa-chevron-down" aria-hidden />
                </button>
              )}
            </div>
            {panelOpen && (
              <div className="menu-picker__child-panel">
                <div className="menu-picker__panel-head">
                  <span className="menu-picker__panel-summary">
                    <strong>{keptItemIds.length}</strong> {t("menuTreeKeptSummary", { total: items.length })}
                  </span>
                  <button
                    type="button"
                    className="btn menu-picker__panel-toggle"
                    onClick={() =>
                      onKeptItemsChange?.(
                        choice.id,
                        keptItemIds.length === items.length ? [] : itemIds
                      )
                    }
                  >
                    {keptItemIds.length === items.length
                      ? t("menuTreeUncheckAll")
                      : t("menuTreeCheckAll")}
                  </button>
                </div>
                <div className="menu-picker__products">
                  {items.map((item) => {
                    const on = keptItemIds.includes(item.id);
                    const itemSwitchId = `${switchId}-${item.id}`;
                    const initial = String(item.label || "?").trim().charAt(0).toUpperCase();
                    return (
                      <label
                        key={item.id}
                        htmlFor={itemSwitchId}
                        className={`menu-picker__product ${on ? "is-on" : ""}`}
                      >
                        <span className="menu-picker__avatar" aria-hidden>
                          {initial || "?"}
                        </span>
                        <span className="menu-picker__product-label">{item.label}</span>
                        <input
                          id={itemSwitchId}
                          type="checkbox"
                          className="menu-picker__switch"
                          role="switch"
                          checked={on}
                          aria-checked={on}
                          onChange={() =>
                            onKeptItemsChange?.(
                              choice.id,
                              on
                                ? keptItemIds.filter((id) => id !== item.id)
                                : [...keptItemIds, item.id]
                            )
                          }
                        />
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ProductChoiceKeepPanel;
